import { useParams, Link } from 'react-router-dom';
import { useState } from 'react';
import { useSEO } from '@/hooks/useSEO';
import { videos, categories } from '@/mocks/videos';
import VideoCard from '@/components/base/VideoCard';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug) || categories[0];
  const filteredVideos = videos.filter((v) => v.categorySlug === slug);
  const [sortBy, setSortBy] = useState<'latest' | 'rating' | 'views'>('latest');

  const sortedVideos = [...filteredVideos].sort((a, b) => {
    if (sortBy === 'rating') return b.rating - a.rating;
    if (sortBy === 'views') return b.views - a.views;
    return new Date(b.publishDate).getTime() - new Date(a.publishDate).getTime();
  });

  useSEO({
    title: `${category.name} - 神马视频高清免费在线观看 | ${category.name}电影电视剧全集`,
    description: `神马视频${category.name}频道为您提供海量高清影视内容免费在线观看，电影电视剧全集不缓冲播放，每日更新，极速加载。`,
    keywords: `${category.name},神马视频,免费在线观看,高清不缓冲,电影全集,视频在线观看,水果视频网站`,
  });

  const breadcrumbs = [
    { name: '首页', href: '/' },
    { name: category.name, href: `/category/${slug}` },
  ];

  return (
    <>
      <div className="bg-background-100 border-b border-background-200">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-3">
          <nav aria-label="面包屑导航" className="flex items-center gap-2 text-xs text-foreground-500">
            {breadcrumbs.map((crumb, i) => (
              <span key={crumb.href} className="flex items-center gap-2">
                {i > 0 && <i className="ri-arrow-right-s-line"></i>}
                {i < breadcrumbs.length - 1 ? (
                  <Link to={crumb.href} className="hover:text-primary-600 transition-colors">{crumb.name}</Link>
                ) : (
                  <span className="text-foreground-700 font-medium">{crumb.name}</span>
                )}
              </span>
            ))}
          </nav>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-8 md:py-12">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading">
              {category.name}
            </h1>
            <p className="text-sm text-foreground-500 mt-1">
              共 {filteredVideos.length} 部影片 · 高清免费在线观看
            </p>
          </div>
          <div className="flex items-center gap-2">
            {(['latest', 'rating', 'views'] as const).map((key) => (
              <button
                key={key}
                onClick={() => setSortBy(key)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                  sortBy === key
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                {key === 'latest' && '最新'}
                {key === 'rating' && '评分'}
                {key === 'views' && '热度'}
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap gap-3 mb-8">
          {categories.map((cat) => (
            <Link
              key={cat.slug}
              to={`/category/${cat.slug}`}
              className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                cat.slug === slug
                  ? 'bg-secondary-500 text-background-50'
                  : 'bg-background-100 text-foreground-600 hover:bg-background-200 hover:text-foreground-900'
              }`}
            >
              <i className={`${cat.icon} mr-1.5`}></i>
              {cat.name}
            </Link>
          ))}
        </div>

        {sortedVideos.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
            {sortedVideos.map((video) => (
              <VideoCard key={video.id} video={video} />
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-film-line text-3xl text-foreground-400"></i>
            </div>
            <p className="mt-4 text-foreground-500">该分类暂无内容，敬请期待</p>
          </div>
        )}
      </div>

      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": breadcrumbs.map((crumb, i) => ({
            "@type": "ListItem",
            "position": i + 1,
            "name": crumb.name,
            "item": `https://www.shenmashipin.com${crumb.href}`
          }))
        })
      }} />

      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          "name": `${category.name} - 神马视频`,
          "description": `神马视频${category.name}频道提供海量高清影视内容免费在线观看。`,
          "url": `https://www.shenmashipin.com/category/${slug}`,
          "isPartOf": {
            "@type": "WebSite",
            "name": "神马视频",
            "url": "https://www.shenmashipin.com/"
          }
        })
      }} />
    </>
  );
}